import type { DistanceTier, PostKind } from '@/api/types';
import { t, tNow } from '@/i18n/languageStore';

/** Category chips offered when creating a post (the design's CATEGORIES; same list as Android). */
export const HELP_CATEGORIES = [
  'Errands',
  'Transport',
  'Education',
  'Yard work',
  'Moving',
  'Tech help',
  'Other',
];

/** The design copy for a post's kind badge. */
export function kindLabel(kind: PostKind): string {
  return kind === 'need' ? tNow('Needs help') : tNow('Can help');
}

/** "Vouched by 3 neighbors": how many neighbors vouched for someone. */
export function vouchLabel(count: number): string {
  if (count <= 0) return t('Not vouched yet');
  return count === 1 ? t('Vouched by 1 neighbor') : t('Vouched by {n} neighbors', { n: count });
}

/** Short relative time ("Just now", "5m ago", "2h ago", "3d ago"), as in the design's feed. */
export function formatRelativeTime(iso: string | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor((now - then) / 60000);
  if (minutes < 1) return tNow('Just now');
  if (minutes < 60) return tNow('{minutes}m ago', { minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return tNow('{hours}h ago', { hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return tNow('{days}d ago', { days });
  return new Date(then).toLocaleDateString();
}

const TIER_LABELS: Record<DistanceTier, string> = {
  within_1: 'Under 1 km',
  '1_3': '1–3 km',
  '3_10': '3–10 km',
  '10_25': '10–25 km',
  over_25: 'Over 25 km',
};

/** The distance as a tier ("1–3 km"); from an older server, the figure ("1.2 km"); null when unknown. */
export function formatDistanceTier(
  tier: DistanceTier | null | undefined,
  km: number | null | undefined,
): string | null {
  return tier ? TIER_LABELS[tier] : formatDistance(km);
}

/** "1.2 km", or null when the backend could not compute a distance. */
export function formatDistance(km: number | null | undefined): string | null {
  return km == null ? null : `${km} km`;
}
